import { useId } from 'react';
import type { PulseData } from '../utils/pulse';

/**
 * The Pulse (#489 Phase 2c) — the Session Story signature viz.
 *
 * An activity EKG drawn on a session's time axis: a diverging token waveform
 * with output tokens swelling above a centerline (the agent producing) and
 * input tokens below (the agent consuming), warm pips marking genuine human
 * prompts on the centerline (the heartbeat — who's driving), and flat
 * stretches where the session was idle. Sitting boundaries render as faint
 * resumption seams with the gap labeled.
 *
 * Fractal by design: the same component draws the full session hero, a single
 * chapter, and (via `variant="mini"`) a compact spark on a Feed card.
 *
 * Built from theme tokens (sky = agent-cool swell, orange = human-warm pips)
 * so it belongs to the gp-ui system, not the IA prototype it descends from.
 *
 * v1 renders tokens + prompts + idle + seams. Tool-call intensity is carried
 * on each sample (`PulseSample.tools`) but not yet drawn — it's the first
 * "tweak with real data" overlay per docs/changes/2026-07-17-session-story-ia-design.md.
 */

export type PulseVariant = 'full' | 'mini';

interface PulseProps {
  data: PulseData;
  variant?: PulseVariant;
  /** SVG height in px. Defaults: full=72, mini=28. */
  height?: number;
  className?: string;
  ariaLabel?: string;
}

// Fixed pixel width per time bucket. The chart's total width therefore grows
// with the number of buckets (i.e. with session/chapter duration) rather than
// stretching to fill its container — so a long session reads as visibly wider
// than a short one, and a chapter's width is proportional to its parent
// session. Wide charts scroll inside an overflow-x container.
const SLOT_PX: Record<PulseVariant, number> = { full: 8, mini: 3 };

export function Pulse({ data, variant = 'full', height, className, ariaLabel }: PulseProps) {
  const clipId = useId();
  const h = height ?? (variant === 'mini' ? 28 : 72);
  const { samples, promptIndices, segments } = data;
  const n = samples.length;

  if (n === 0) return null;

  const slot = SLOT_PX[variant];
  const VIEW_W = n * slot;
  // One bar per time bucket, constant width (height is the only value channel).
  const barW = Math.max(slot * (variant === 'mini' ? 0.9 : 0.62), 0.6);
  const barOffset = (slot - barW) / 2;
  const radius = Math.min(barW / 2, variant === 'mini' ? 1 : 1.5);
  const minBarH = variant === 'mini' ? 1 : 2;

  const showPips = variant === 'full' || promptIndices.length <= n / 3;
  const pipR = variant === 'mini' ? 1 : 1.8;
  const topPad = variant === 'mini' ? 1 : 2;
  // Prompt pips sit in a band at the bottom, a hair of space below the bars.
  const pipBand = showPips ? pipR * 2 + 3 : 0;
  const baseline = h - pipBand;
  const barMax = baseline - topPad; // max total height of a stacked bar

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${h}`}
      width={VIEW_W}
      height={h}
      role="img"
      aria-label={ariaLabel ?? 'Session activity pulse'}
      className={className}
      style={{ display: 'block' }}
    >
      <defs>
        <clipPath id={clipId}>
          <rect x="0" y="0" width={VIEW_W} height={h} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${clipId})`}>
        {/* Resumption seams: a faint vertical divider at every sitting boundary
            (the split is always real; the gap label is drawn on top below when
            we can compute it). */}
        {segments.map((seg) =>
          seg.start > 0 ? (
            <line
              key={`seam-${seg.start}`}
              x1={seg.start * slot}
              y1={topPad}
              x2={seg.start * slot}
              y2={baseline}
              className="stroke-gray-300 dark:stroke-gray-600"
              strokeWidth="1"
              strokeDasharray="2 3"
            />
          ) : null,
        )}

        {/* Activity bars — one per bucket, height = token volume on the global
            scale, split into output (blue, top) over input (purple, bottom). */}
        {samples.map((s, i) => {
          if (s.idle) return null;
          const total = s.out + s.in;
          if (total <= 0) return null;
          const x = i * slot + barOffset;
          const barH = Math.max(total * barMax, minBarH);
          const outSeg = barH * (s.out / total);
          const inSeg = barH - outSeg;
          const inputY = baseline - inSeg;
          const outputY = inputY - outSeg;
          return (
            <g key={i}>
              {inSeg > 0 && (
                <rect x={x} y={inputY} width={barW} height={inSeg} rx={radius} className="fill-violet-400 dark:fill-violet-400" />
              )}
              {outSeg > 0 && (
                <rect x={x} y={outputY} width={barW} height={outSeg} rx={radius} className="fill-sky-500 dark:fill-sky-400" />
              )}
            </g>
          );
        })}

        {/* Human prompt pips — a row just below the bars (the heartbeat). */}
        {showPips &&
          promptIndices.map((i) => (
            <circle
              key={`pip-${i}`}
              cx={i * slot + slot / 2}
              cy={h - pipR}
              r={pipR}
              className="fill-orange-500 dark:fill-orange-400"
            />
          ))}

        {/* Seam labels on top so the bars don't occlude them; centered on the line. */}
        {variant === 'full' &&
          segments.map((seg) =>
            seg.seamLabel && seg.start > 0 ? (
              <text
                key={`lbl-${seg.start}`}
                x={seg.start * slot}
                y={10}
                textAnchor="middle"
                className="fill-gray-500 dark:fill-gray-300 stroke-white dark:stroke-navy-900"
                style={{ fontSize: 11, paintOrder: 'stroke', strokeWidth: 3, strokeLinejoin: 'round' }}
              >
                {seg.seamLabel}
              </text>
            ) : null,
          )}
      </g>
    </svg>
  );
}
